import { Box, CircularProgress, Grid, MenuItem, TextField, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Formtitle, IperiaLayout } from '../../components'
import ParentExtractedDataEdit from '../../components/iperia/ParentExtractedDataEdit'
import { useIperiaStatus } from '../../hooks/useIperiaStatus'
import { IperiaFormDefaultState } from '../../utils/constants'
import {
  buildParentEmployeurInfoPayload,
  isValidOptionalHourlyWage,
} from '../../utils/iperiaParentEmployeur'
import type { IperiaFormType } from '../../utils/sharedTypes'
import { emailValidator, pajemploiNumberValidator, phoneNumberValidator } from '../../utils/validators'

const PAJEMPLOI_FORMAT_ERROR = 'Le numéro Pajemploi doit commencer par Y suivi de 10 à 14 chiffres'

const TITLE_CHOICES = [
  { value: 'M.', label: 'M.' },
  { value: 'Mme', label: 'Mme' },
]

type ParentInfoFields =
  | 'parent_title'
  | 'parent_first_name'
  | 'parent_last_name'
  | 'parent_email'
  | 'parent_mobile_phone'
  | 'parent_pajemploi_number'
  | 'hourly_wage'

const ParentEmployeurInfo = () => {
  const navigate = useNavigate()
  const { global_id: globalId } = useParams<{ global_id: string }>()
  const { status, loading, error, updateParentEmployeur } = useIperiaStatus()
  
  const [form, setForm] = useState<IperiaFormType>(IperiaFormDefaultState)
  const [errors, setErrors] = useState<Partial<Record<ParentInfoFields, string>>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [editingExtractedData, setEditingExtractedData] = useState(
    () => !status?.parent_employeur?.parent_first_name && !status?.parent_employeur?.parent_last_name,
  )

  const steps = [
    { label: 'Informations', url: 'parent-employeur' },
    { label: 'Adresse', url: 'parent-employeur/adresse' },
  ]

  useEffect(() => {
    if (!status?.parent_employeur) return
    
    setForm({ 
      ...IperiaFormDefaultState, 
      data: { 
        ...IperiaFormDefaultState.data, 
        parent_title: status.parent_employeur.parent_title || '',
        parent_first_name: status.parent_employeur.parent_first_name || '',
        parent_last_name: status.parent_employeur.parent_last_name || '',
        parent_email: status.parent_employeur.parent_email || '',
        parent_mobile_phone: status.parent_employeur.parent_mobile_phone || '',
        parent_pajemploi_number: status.parent_employeur.parent_pajemploi_number || '',
        hourly_wage:
          status.parent_employeur.hourly_wage != null && status.parent_employeur.hourly_wage !== ''
            ? String(status.parent_employeur.hourly_wage)
            : '',
      },
      steps,
    })
  }, [status?.parent_employeur])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !status) {
    return (
      <Typography color="error" sx={{ p: 2 }}>
        Impossible de charger votre dossier.
      </Typography>
    )
  }

  const handleChange = (field: ParentInfoFields, value: string) => {
    setForm((prev) => ({ ...prev, data: { ...prev.data, [field]: value } }))
    
    // Real-time validation
    setErrors((prev) => {
      const next = { ...prev }
      delete next[field]

      if (field === 'parent_email' && value && !emailValidator(value)) {
        next[field] = 'Veuillez renseigner un e-mail valide'
      } else if (
        field === 'parent_email'
        && value
        && status?.learner_summary?.email
        && value.trim().toLowerCase() === status.learner_summary.email.trim().toLowerCase()
      ) {
        next[field] = "L'email du parent employeur doit être différent de celui de l'apprenant(e)"
      } else if (field === 'parent_mobile_phone' && value && !phoneNumberValidator(value)) {
        next[field] = 'Veuillez renseigner un numéro de téléphone valide'
      } else if (field === 'parent_title' && !value.trim()) {
        next[field] = 'La civilité est requise'
      } else if (field === 'parent_first_name' && !value.trim()) {
        next[field] = 'Le prénom est requis'
      } else if (field === 'parent_last_name' && !value.trim()) {
        next[field] = 'Le nom est requis'
      } else if (field === 'parent_pajemploi_number' && !value.trim()) {
        next[field] = 'Le numéro Pajemploi est requis'
      } else if (field === 'parent_pajemploi_number' && value.trim() && !pajemploiNumberValidator(value)) {
        next[field] = PAJEMPLOI_FORMAT_ERROR
      } else if (field === 'hourly_wage' && value && !isValidOptionalHourlyWage(value)) {
        next[field] = 'Veuillez renseigner un salaire horaire valide (nombre positif ou nul)'
      }
      return next
    })
  }

  const validate = (): boolean => {
    const newErrors: Partial<Record<ParentInfoFields, string>> = {}

    if (!form.data.parent_title) {
      newErrors.parent_title = 'La civilité est requise'
    }
    if (!form.data.parent_first_name?.trim()) {
      newErrors.parent_first_name = 'Le prénom est requis'
    }
    if (!form.data.parent_last_name?.trim()) {
      newErrors.parent_last_name = 'Le nom est requis'
    }
    if (!form.data.parent_email) {
      newErrors.parent_email = "L'email est requis"
    } else if (!emailValidator(form.data.parent_email)) {
      newErrors.parent_email = 'Veuillez renseigner un e-mail valide'
    } else if (
      status.learner_summary?.email
      && form.data.parent_email.trim().toLowerCase() === status.learner_summary.email.trim().toLowerCase()
    ) {
      newErrors.parent_email = "L'email du parent employeur doit être différent de celui de l'apprenant(e)"
    }
    if (!form.data.parent_mobile_phone) {
      newErrors.parent_mobile_phone = 'Le téléphone portable est requis'
    } else if (!phoneNumberValidator(form.data.parent_mobile_phone)) {
      newErrors.parent_mobile_phone = 'Veuillez renseigner un numéro de téléphone valide'
    }
    if (!form.data.parent_pajemploi_number?.trim()) {
      newErrors.parent_pajemploi_number = 'Le numéro Pajemploi est requis'
    } else if (!pajemploiNumberValidator(form.data.parent_pajemploi_number)) {
      newErrors.parent_pajemploi_number = PAJEMPLOI_FORMAT_ERROR
    }
    if (form.data.hourly_wage && !isValidOptionalHourlyWage(form.data.hourly_wage)) {
      newErrors.hourly_wage = 'Veuillez renseigner un salaire horaire valide (nombre positif ou nul)'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = async () => {
    // Le bouton reste actif : le toucher affiche ce qui manque et amène au premier champ en erreur,
    // au lieu d'un bouton grisé sans explication.
    if (!validate()) {
      window.setTimeout(() => {
        document.querySelector('[aria-invalid="true"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 0)
      return
    }
    if (!globalId) {
      setSubmitError('Identifiant du dossier manquant.')
      return
    }
    
    setSubmitting(true)
    setSubmitError(null)
    
    try {
      await updateParentEmployeur(buildParentEmployeurInfoPayload(form.data))
      
      navigate(`/iperia/${globalId}/parent-employeur/adresse`)
    } catch (err) {
      setSubmitError('Une erreur est survenue lors de la sauvegarde.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleBack = () => {
    if (!globalId) return
    navigate(`/iperia/${globalId}/documents`)
  }

  return (
    <IperiaLayout 
      productName={form.data.product_name} 
      handleBack={handleBack} 
      handleNext={handleNext} 
      submitting={submitting} 
      submittingNext={submitting}
      alreadyConfirmed={status.can_sign_bi || status.workflow_status === 'bi_blocked_late_start' || !!status.signature_blocked_message}
    >
      <Formtitle
        title="Informations du parent employeur"
        subtitle="Veuillez renseigner les informations du parent employeur qui vous emploie."
      />
      <Typography sx={{ mb: 5 }}>
        Assurez-vous que les coordonnées ci-dessous correspondent au bulletin déposé à l’étape précédente.
        <br />
        Si vous souhaitez indiquer un parent employeur différent, veuillez uploader un nouveau bulletin sur le formulaire de dépôt de documents.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Civilité"
            required
            value={form.data.parent_title || ''}
            onChange={(e) => handleChange('parent_title', e.target.value)}
            disabled={submitting}
            error={!!errors.parent_title}
            helperText={errors.parent_title}
          >
            {TITLE_CHOICES.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Nom"
            inputProps={{ autoComplete: 'off' }}
            required
            value={form.data.parent_last_name || ''}
            onChange={(e) => handleChange('parent_last_name', e.target.value)}
            disabled={submitting || !editingExtractedData}
            error={!!errors.parent_last_name}
            helperText={errors.parent_last_name}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Prénom"
            inputProps={{ autoComplete: 'off' }}
            required
            value={form.data.parent_first_name || ''}
            onChange={(e) => handleChange('parent_first_name', e.target.value)}
            disabled={submitting || !editingExtractedData}
            error={!!errors.parent_first_name}
            helperText={errors.parent_first_name}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Numéro Pajemploi"
            inputProps={{ autoComplete: 'off', autoCapitalize: 'characters' }}
            required
            value={form.data.parent_pajemploi_number || ''}
            onChange={(e) => handleChange('parent_pajemploi_number', e.target.value)}
            disabled={
              submitting
              || (!editingExtractedData && Boolean(status.parent_employeur?.parent_pajemploi_number))
            }
            error={!!errors.parent_pajemploi_number}
            helperText={errors.parent_pajemploi_number}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Salaire horaire net (€)"
            inputProps={{ inputMode: 'decimal' }}
            type="text"
            value={form.data.hourly_wage || ''}
            onChange={(e) => handleChange('hourly_wage', e.target.value)}
            disabled={submitting}
            error={!!errors.hourly_wage}
            helperText={errors.hourly_wage}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Téléphone portable"
            inputProps={{ autoComplete: 'off', inputMode: 'tel' }}
            required
            type="tel"
            value={form.data.parent_mobile_phone || ''}
            onChange={(e) => handleChange('parent_mobile_phone', e.target.value)}
            disabled={submitting}
            error={!!errors.parent_mobile_phone}
            helperText={errors.parent_mobile_phone}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Email"
            inputProps={{ autoComplete: 'off', autoCapitalize: 'none' }}
            required
            type="email"
            value={form.data.parent_email || ''}
            onChange={(e) => handleChange('parent_email', e.target.value)}
            disabled={submitting}
            error={!!errors.parent_email}
            helperText={errors.parent_email}
          />
        </Grid>
      </Grid>

      {globalId && !editingExtractedData && (
        <Box sx={{ mb: 3 }}>
          <ParentExtractedDataEdit
            onEnabled={() => setEditingExtractedData(true)}
          />
        </Box>
      )}

      {submitError && (
        <Typography color="error" sx={{ mb: 2 }}>
          {submitError}
        </Typography>
      )}
    </IperiaLayout>
  )
}

export default ParentEmployeurInfo
